import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, Outlet, redirect } from '@tanstack/react-router';
import { fetchSession, type SessionData } from '../lib/auth-client';
import { AppLayout } from '../shell/AppLayout';
import { NotFoundPage, RootErrorPage } from '../shell/RootPages';

export type RouterContext = { queryClient: QueryClient };
export const SESSION_QUERY_KEY = ['auth', 'session'] as const;

export function loadSession(queryClient: QueryClient): Promise<SessionData | null> {
  return queryClient.fetchQuery({ queryKey: SESSION_QUERY_KEY, queryFn: fetchSession, staleTime: 60_000 });
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  errorComponent: RootErrorPage,
  notFoundComponent: NotFoundPage,
});

/** Signed-in app (top bar shell). Path-less: children define full paths. */
export const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    const session = await loadSession(context.queryClient);
    if (!session) throw redirect({ to: '/sign-in', search: { redirect: location.href } });
    return { session };
  },
  component: AppLayout,
});

/** Public pages (pricing, sign-in, sign-up, invite): no shell, no guard. */
export const publicLayoutRoute = createRoute({ getParentRoute: () => rootRoute, id: 'public', component: Outlet });

/** Signed in but outside the shell (onboarding / first workspace). */
export const bareAuthedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'bare',
  beforeLoad: async ({ context, location }) => {
    const session = await loadSession(context.queryClient);
    if (!session) throw redirect({ to: '/sign-in', search: { redirect: location.href } });
    return { session };
  },
  component: Outlet,
});
